import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ComplianceValidationStatusTypeEnum } from 'src/app/compliance/compliance-report/enums/compliance-validation-status-type.enum';
import { ValidationQueueDetail } from 'src/app/compliance/validation-queue/details/models/validation-queue-detail';
import { ValidationQueueFile } from 'src/app/compliance/validation-queue/details/models/validation-queue-file';
import { ValidationQueueGridLookups } from 'src/app/compliance/validation-queue/grid/models/validation-queue-grid-lookups';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { DocumentAttribute } from 'src/app/shared/lib/compliance-shared/document-attributes-form/document-attributes-form.store';
import { FetchArgs, PagingToken, PagingTokenFixed } from 'src/app/shared/models/core';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { combine } from 'src/app/shared/utilities/route';
import { AIVendorFeedbackFromCL } from '../../models/validation-status.model';
import { ValidationQueueBypassedDetail } from '../details/models/validation-queue-bypassed-detail';
import { ValidationQueueGridItem } from '../grid/models/validation-queue-grid-item';

/**
 * @deprecated Do not use this service.  Use service(s) from "src/app/compliance/api" or "src/app/compliance/api/migration"
 */
@Injectable({ providedIn: 'root' })
export class ValidationQueueService {
    private coreApiPath: string;

    constructor(@Inject(APP_CONFIG) settings: Settings, private http: HttpClient) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova/compliance/validation-queue');
    }

    // ** ComplianceValidationQueueController.GetGridLookupDataAsync
    getGridLookups(): Observable<ValidationQueueGridLookups> {
        const url = `${this.coreApiPath}/lookup`;
        return this.http.get<ValidationQueueGridLookups>(url);
    }

    // ** ComplianceValidationQueueController.GetDeclineCategoryLookups
    getDeclineCategoryLookups(): Observable<KeyValue<number, string>[]> {
        const url = `${this.coreApiPath}/decline-categories`;
        return this.http.get<KeyValue<number, string>[]>(url);
    }

    // ** ComplianceValidationQueueController.GetByContractId
    getByContractId(contractId: number): Observable<ValidationQueueDetail> {
        const url = `${this.coreApiPath}/${contractId}`;
        return this.http.get<ValidationQueueDetail>(url);
    }

    // ** ComplianceValidationQueueController.GetBypassedDocsByContractId
    getBypassedDocs(contractId: number): Observable<PagingToken<ValidationQueueBypassedDetail[]>> {
        const url = `${this.coreApiPath}/bypassed/${contractId}`;
        return this.http.get<PagingToken<ValidationQueueBypassedDetail[]>>(url);
    }

    // ** ComplianceValidationQueueController.GetBypassedDocsCountByContractId
    getBypassedDocsCount(contractId: number): Observable<number> {
        const url = `${this.coreApiPath}/bypassed/${contractId}/count`;
        return this.http.get<number>(url);
    }

    // ** ComplianceValidationQueueController.MarkDocsAsViewed
    sendBypassedDocumentsToMarkAsViewed(docIds: number[]): Observable<any> {
        const url = `${this.coreApiPath}/mark-docs-as-viewed`;
        return this.http.post<any>(url, docIds);
    }

    // ** ComplianceValidationQueueController.GetFileById
    getFileById(contractId: number, id: number): Observable<ValidationQueueFile> {
        const url = `${this.coreApiPath}/${contractId}/files/${id}`;
        return this.http.get<ValidationQueueFile>(url);
    }

    // ** ComplianceValidationQueueController.GetHospitalSystems
    getHospitalSystems(filter: string, take: number): Observable<KeyValue<number, string>[]> {
        const url = `${this.coreApiPath}/hospital-systems`;
        const params = new HttpParams().set('filter', filter).set('take', take.toString());
        return this.http.get<KeyValue<number, string>[]>(url, { params: params });
    }

    // ** ComplianceValidationQueueController.SearchPostAsync
    searchValidationQueue(args: FetchArgs): Observable<PagingTokenFixed<ValidationQueueGridItem[]>> {
        const url = `${this.coreApiPath}/search`;
        return this.getPagedNew<ValidationQueueGridItem>(url, args);
    }

    protected getPagedNew<T>(url: string, args: FetchArgs): Observable<PagingTokenFixed<T[]>> {
        return this.http.post<PagingTokenFixed<T[]>>(url, args).pipe(
            map((resp) => {
                return resp;
            })
        );
    }

    // ** ComplianceValidationQueueController.SetValidationQueueFileStatus
    setValidationQueueFileStatus(
        id: number,
        contractId: number,
        statusId: number,
        candidateDocumentId?: number,
        documentTypeId?: number,
        expirationDateUtc?: Date,
        documentAttributes?: DocumentAttribute[],
        rejectionNote?: string,
        sendEmailToCS?: boolean,
        unacceptDocument?: boolean,
        rejectionCategory?: KeyValue<number, string>
    ) {
        const url = `${this.coreApiPath}/${contractId}/files/${id}`;
        const data = {
            id: id,
            statusId: statusId,
            candidateDocumentId: candidateDocumentId,
            documentTypeId: documentTypeId,
            expirationDateUtc: expirationDateUtc,
            documentAttributes: documentAttributes
        };

        if (statusId === ComplianceValidationStatusTypeEnum.Declined) {
            data['rejectionNote'] = rejectionNote;
            data['sendEmailToCredentialSpecialist'] = sendEmailToCS;
            data['unacceptDocument'] = unacceptDocument ?? false;
            data['rejectionCategory'] = rejectionCategory ?? null;
        }

        return this.http.put(url, data);
    }

    // ** ComplianceValidationQueueController.SendAIVendorFeedback
    sendAIVendorFeedback(feedback: AIVendorFeedbackFromCL): Observable<unknown> {
        return this.http.post<unknown>(`${this.coreApiPath}/ai-vendor-feedback`, feedback);
    }
}
